import { isString } from 'lodash';
import { useLocation } from 'react-router-dom';
import React, { useMemo, useState, useEffect, useContext, createContext } from 'react';

import { usePathname, useSearchParams } from 'src/routes/hooks';

import { formatScreenName } from 'src/utils/format-screen-name';

import { getClientId } from 'src/init-firebase';
import { eventsDict } from 'src/_mock/_analytics';
import { useAuthStore } from 'src/auth/auth.store';
import { useUserStore } from 'src/stores/user/user.store';
import { useAnalyticsStore } from 'src/stores/analytics/analytics.store';
import { initClarity, setClarityPageTag, identifyClarityUser } from 'src/init-clarity';

import { eventKeysType } from 'src/types/analytics/analytics';

interface AnalyticsContextType {
  actions: any;
  logAction: (
    actionName: eventKeysType,
    param?: string,
    secondParam?: string,
    target?: string[],
    otherData?: Object
  ) => void;
}

export const AnalyticsContext = createContext<AnalyticsContextType | undefined>(undefined);

const saveActionsToLocalStorage = (actions: any) => {
  localStorage.setItem('userActions', JSON.stringify(actions));
};

type AnalyticsProviderProps = {
  children: React.ReactNode;
};

export const AnalyticsProvider = ({ children }: AnalyticsProviderProps) => {
  const pathname = usePathname();
  const location = useLocation();
  const searchParams = useSearchParams();
  const { sendLogs } = useAnalyticsStore();
  const { user } = useUserStore();
  const { accessToken, refreshToken } = useAuthStore();
  const [actions, setActions] = useState(() => {
    try {
      const saved = localStorage.getItem('userActions');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    initClarity();
  }, []);

  useEffect(() => {
    const token = searchParams.get('token');
    const pageName = document.title?.trim() || pathname;
    const userTier = user?.userLevel ?? (user as any)?.tier;
    const backendUserId = (user as any)?.id ?? (user as any)?._id;
    const clarityUserId = token || backendUserId;

    if (pageName) {
      setClarityPageTag(pageName);
    }

    if (clarityUserId) {
      identifyClarityUser(String(clarityUserId), userTier, user?.name, pathname);
    }
  }, [searchParams, user?.userLevel, user?.name, pathname, user]);

  const logAction = (
    actionName: eventKeysType,
    param?: string,
    secondParam?: string,
    target?: string[],
    otherData?: Object
  ) => {
    const clientId = getClientId();
    const userId = user?.cpf ? btoa(user.cpf) : undefined;

    let action;
    if (actionName === 'defaultNav') {
      action = {
        ...eventsDict[actionName],
        key: actionName,
        screenName: param || '',
        parameter: secondParam || '',
        eventDate: new Date(),
        target:
          target && target.length > 0
            ? target
            : (eventsDict[actionName] as any)?.target || ['roxcode'],
        otherData: {
          ...((eventsDict[actionName] as any)?.otherData || {}),
          ...(otherData || {}),
          clientId,
          userId,
        },
        origin: 'Viva',
      };
    } else {
      action = {
        ...eventsDict[actionName],
        parameter: !isString(eventsDict[actionName].parameter)
          ? eventsDict[actionName].parameter(param, secondParam)
          : eventsDict[actionName].parameter,
        eventDate: new Date(),
        target: target && target.length > 0 ? target : (eventsDict[actionName] as any)?.target,
        otherData: {
          ...((eventsDict[actionName] as any)?.otherData || {}),
          ...(otherData || {}),
          clientId,
          userId,
        },
        origin: 'Viva',
      };
    }
    setActions((prev: any) => [...prev, action]);
  };

  useEffect(() => {
    saveActionsToLocalStorage(actions);
  }, [actions]);

  useEffect(() => {
    if (!location.state?.from && pathname.includes('/home')) {
      logAction('homeNavSuperapp');
    }
    if (location.state?.from === '/home' && pathname === '/user/myProfile') {
      logAction('myVivaNav');
    }

    if (pathname !== '/') {
      const formattedScreenName = formatScreenName(pathname);
      logAction('defaultNav', location.state?.from, pathname, undefined, {
        screenName: formattedScreenName,
        screenFrom: location.state?.from,
        screenPath: pathname,
      });
    }
  }, [pathname]);

  const sendActions = async () => {
    const savedActions = localStorage.getItem('userActions');
    const logs = savedActions ? JSON.parse(savedActions) : [];
    if (logs.length === 0) return;
    if (!(accessToken && refreshToken)) return;

    sendLogs({ logs }).then(() => {
      setActions([]);
      saveActionsToLocalStorage([]);
    });
  };

  useEffect(() => {
    const interval = setInterval(() => {
      if (pathname !== '/auth/authError') {
        sendActions();
      }
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    window.addEventListener('beforeunload', sendActions);

    return () => {
      window.removeEventListener('beforeunload', sendActions);
    };
  }, []);

  const memoizedValue = useMemo(
    () => ({
      actions,
      logAction,
    }),
    [actions, logAction]
  );

  return <AnalyticsContext.Provider value={memoizedValue}>{children}</AnalyticsContext.Provider>;
};

export const useAnalyticsContext = (): AnalyticsContextType => {
  const context = useContext(AnalyticsContext);
  if (!context) {
    throw new Error('useAnalyticsContext deve ser usado dentro de um AnalyticsProvider');
  }
  return context;
};
